/**
 * Password-reset logic shared by the Classic and Floodlight screens.
 *
 *   useForgotPassword()  — /forgot-password: email + terms agreement, sends
 *                          the reset email (AuthContext.resetPassword), then
 *                          returns to sign-in after 3s.
 *   useResetPassword()   — /reset-password?token=…: new password + confirm,
 *                          posts to the processPasswordReset function, then
 *                          returns to sign-in after 2s.
 *
 * v1.70 fixes (both looks): the forgot screen went to 'SignIn', which is not
 * a route (it fell through to Home), and the reset screen called
 * `navigate`, which NavigationContext does not provide (a TypeError on
 * "Back to Login"). Both now go to 'Login'. The reset token is no longer
 * written to the console.
 */
import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';

export const PROCESS_RESET_URL = 'https://us-central1-bet-optimized-fe922.cloudfunctions.net/processPasswordReset';
export const FORGOT_REDIRECT_MS = 3000;
export const RESET_REDIRECT_MS = 2000;
export const MIN_PASSWORD_LENGTH = 6;

export const FORGOT_ERRORS = {
  email: 'Please enter your email address',
  terms: 'Please agree to the Terms and Conditions',
  failed: 'Failed to send reset email. Please try again.',
};

export const RESET_ERRORS = {
  missingToken: 'Invalid or missing reset token',
  empty: 'Please enter a password',
  short: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
  mismatch: 'Passwords do not match',
  token: 'Invalid reset token',
  failed: 'Failed to reset password',
  generic: 'An error occurred. Please try again.',
};

/** First validation error for a new password, or ''. */
export function validateNewPassword(password, confirmPassword, token) {
  if (!password) return RESET_ERRORS.empty;
  if (password.length < MIN_PASSWORD_LENGTH) return RESET_ERRORS.short;
  if (password !== confirmPassword) return RESET_ERRORS.mismatch;
  if (!token) return RESET_ERRORS.token;
  return '';
}

function useRedirectTimer() {
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (fn, ms) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(fn, ms);
  };
}

export function useForgotPassword() {
  const { resetPassword } = useAuth();
  const { navigateTo } = useNavigation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const later = useRedirectTimer();

  const toggleTerms = () => setAgreedToTerms((v) => !v);

  const handleResetPassword = async () => {
    setError('');

    if (!email.trim()) {
      setError(FORGOT_ERRORS.email);
      return;
    }
    if (!agreedToTerms) {
      setError(FORGOT_ERRORS.terms);
      return;
    }

    setLoading(true);
    try {
      const { error: sendError } = await resetPassword(email.trim());
      if (sendError) {
        setError(sendError);
      } else {
        setSuccess(true);
        setError('');
        later(() => navigateTo('Login'), FORGOT_REDIRECT_MS);
      }
    } catch {
      setError(FORGOT_ERRORS.failed);
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => navigateTo('Login');

  return {
    email, setEmail, loading, agreedToTerms, setAgreedToTerms, toggleTerms,
    error, success, handleResetPassword, goToLogin,
  };
}

export function useResetPassword() {
  const { navigateTo } = useNavigation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const later = useRedirectTimer();

  // Read the token from the URL on mount
  useEffect(() => {
    if (Platform.OS === 'web') {
      const tokenParam = new URLSearchParams(window.location.search).get('token');
      if (tokenParam) setToken(tokenParam);
      else setError(RESET_ERRORS.missingToken);
    }
  }, []);

  const handleResetPassword = async () => {
    setError('');

    const invalid = validateNewPassword(password, confirmPassword, token);
    if (invalid) {
      setError(invalid);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(PROCESS_RESET_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || RESET_ERRORS.failed);
      }
      setSuccess(true);
      later(() => navigateTo('Login'), RESET_REDIRECT_MS);
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.message || RESET_ERRORS.generic);
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => navigateTo('Login');
  const goToForgot = () => navigateTo('ForgotPassword');

  return {
    password, setPassword, confirmPassword, setConfirmPassword,
    token, loading, error, success,
    showPassword, setShowPassword, showConfirmPassword, setShowConfirmPassword,
    handleResetPassword, goToLogin, goToForgot,
  };
}
